/**
 * Course template definitions.
 * Each template defines a complete course structure with sections, pages,
 * and module assignments. Selecting a template auto-fills Steps 2 + 3.
 *
 * Categories: generic, domain, module
 */

export const TEMPLATE_CATEGORIES = [
  { id: 'generic', labelKey: 'courseGuide.templateCategory.generic' },
  { id: 'domain', labelKey: 'courseGuide.templateCategory.domain' },
  { id: 'module', labelKey: 'courseGuide.templateCategory.module' },
];

export const COURSE_TEMPLATES = [
  // ===== GENERIC =====
  {
    id: 'text-heavy',
    category: 'generic',
    nameKey: 'courseGuide.template.textHeavy.name',
    descriptionKey: 'courseGuide.template.textHeavy.description',
    icon: 'TextFormat',
    structure: {
      sections: [
        {
          nameKey: 'courseGuide.template.textHeavy.section1',
          pages: [
            { nameKey: 'courseGuide.template.textHeavy.page1', modules: ['text'] },
          ],
        },
        {
          nameKey: 'courseGuide.template.textHeavy.section2',
          pages: [
            { nameKey: 'courseGuide.template.textHeavy.page2', modules: ['text', 'text'] },
            { nameKey: 'courseGuide.template.textHeavy.page3', modules: ['text', 'download'] },
          ],
        },
        {
          nameKey: 'courseGuide.template.textHeavy.section3',
          pages: [
            { nameKey: 'courseGuide.template.textHeavy.page4', modules: ['text'] },
            { nameKey: 'courseGuide.template.textHeavy.page5', modules: ['task'] },
          ],
        },
      ],
    },
  },
  {
    id: 'video-course',
    category: 'generic',
    nameKey: 'courseGuide.template.videoCourse.name',
    descriptionKey: 'courseGuide.template.videoCourse.description',
    icon: 'Video',
    structure: {
      sections: [
        {
          nameKey: 'courseGuide.template.videoCourse.section1',
          pages: [
            { nameKey: 'courseGuide.template.videoCourse.page1', modules: ['text', 'video'] },
          ],
        },
        {
          nameKey: 'courseGuide.template.videoCourse.section2',
          pages: [
            { nameKey: 'courseGuide.template.videoCourse.page2', modules: ['video', 'text'] },
            { nameKey: 'courseGuide.template.videoCourse.page3', modules: ['video', 'task'] },
          ],
        },
        {
          nameKey: 'courseGuide.template.videoCourse.section3',
          pages: [
            { nameKey: 'courseGuide.template.videoCourse.page4', modules: ['video', 'text'] },
            { nameKey: 'courseGuide.template.videoCourse.page5', modules: ['task'] },
          ],
        },
      ],
    },
  },
  {
    id: 'blended',
    category: 'generic',
    nameKey: 'courseGuide.template.blended.name',
    descriptionKey: 'courseGuide.template.blended.description',
    icon: 'Edit',
    structure: {
      sections: [
        {
          nameKey: 'courseGuide.template.blended.section1',
          pages: [
            { nameKey: 'courseGuide.template.blended.page1', modules: ['text', 'image'] },
          ],
        },
        {
          nameKey: 'courseGuide.template.blended.section2',
          pages: [
            { nameKey: 'courseGuide.template.blended.page2', modules: ['video', 'text'] },
            { nameKey: 'courseGuide.template.blended.page3', modules: ['text', 'download'] },
          ],
        },
        {
          nameKey: 'courseGuide.template.blended.section3',
          pages: [
            { nameKey: 'courseGuide.template.blended.page4', modules: ['discussion'] },
            { nameKey: 'courseGuide.template.blended.page5', modules: ['task'] },
          ],
        },
      ],
    },
  },

  // ===== DOMAIN =====
  {
    id: 'onboarding',
    category: 'domain',
    nameKey: 'courseGuide.template.onboarding.name',
    descriptionKey: 'courseGuide.template.onboarding.description',
    icon: 'Users',
    structure: {
      sections: [
        {
          nameKey: 'courseGuide.template.onboarding.section1',
          pages: [
            { nameKey: 'courseGuide.template.onboarding.page1', modules: ['text', 'video'] },
            { nameKey: 'courseGuide.template.onboarding.page2', modules: ['text', 'image'] },
          ],
        },
        {
          nameKey: 'courseGuide.template.onboarding.section2',
          pages: [
            { nameKey: 'courseGuide.template.onboarding.page3', modules: ['text', 'download'] },
            { nameKey: 'courseGuide.template.onboarding.page4', modules: ['text', 'task'] },
          ],
        },
        {
          nameKey: 'courseGuide.template.onboarding.section3',
          pages: [
            { nameKey: 'courseGuide.template.onboarding.page5', modules: ['text', 'task'] },
          ],
        },
      ],
    },
  },
  {
    id: 'safety-training',
    category: 'domain',
    nameKey: 'courseGuide.template.safety.name',
    descriptionKey: 'courseGuide.template.safety.description',
    icon: 'Safety',
    structure: {
      sections: [
        {
          nameKey: 'courseGuide.template.safety.section1',
          pages: [
            { nameKey: 'courseGuide.template.safety.page1', modules: ['text', 'video'] },
            { nameKey: 'courseGuide.template.safety.page2', modules: ['text', 'image'] },
          ],
        },
        {
          nameKey: 'courseGuide.template.safety.section2',
          pages: [
            { nameKey: 'courseGuide.template.safety.page3', modules: ['video', 'text'] },
            { nameKey: 'courseGuide.template.safety.page4', modules: ['text', 'download'] },
          ],
        },
        {
          nameKey: 'courseGuide.template.safety.section3',
          pages: [
            { nameKey: 'courseGuide.template.safety.page5', modules: ['task'] },
          ],
        },
      ],
      suggestQuiz: true,
      suggestDiploma: true,
    },
  },

  // ===== MODULE-FOCUSED =====
  {
    id: 'interactive-workshop',
    category: 'module',
    nameKey: 'courseGuide.template.workshop.name',
    descriptionKey: 'courseGuide.template.workshop.description',
    icon: 'Forum',
    structure: {
      sections: [
        {
          nameKey: 'courseGuide.template.workshop.section1',
          pages: [
            { nameKey: 'courseGuide.template.workshop.page1', modules: ['text', 'video'] },
          ],
        },
        {
          nameKey: 'courseGuide.template.workshop.section2',
          pages: [
            { nameKey: 'courseGuide.template.workshop.page2', modules: ['task'] },
            { nameKey: 'courseGuide.template.workshop.page3', modules: ['discussion'] },
            { nameKey: 'courseGuide.template.workshop.page4', modules: ['task'] },
          ],
        },
        {
          nameKey: 'courseGuide.template.workshop.section3',
          pages: [
            { nameKey: 'courseGuide.template.workshop.page5', modules: ['discussion', 'task'] },
          ],
        },
      ],
    },
  },
];

/**
 * Convert a template to guide data (tocStructure + pageModuleAssignments).
 * @param {Object} template - A template from COURSE_TEMPLATES
 * @param {Function} t - i18next translation function
 * @returns {{ tocStructure: Array, pageModuleAssignments: Object }}
 */
export const applyTemplate = (template, t) => {
  let counter = 1;
  const tocStructure = [];
  const pageModuleAssignments = {};

  for (const section of template.structure.sections) {
    const sectionId = `temp-section-${counter++}`;
    const pages = [];

    for (const page of section.pages) {
      const pageId = `temp-page-${counter++}`;
      pages.push({ id: pageId, name: t(page.nameKey) });

      if (page.modules?.length) {
        pageModuleAssignments[pageId] = page.modules.map((moduleType) => ({ moduleType }));
      }
    }

    tocStructure.push({ id: sectionId, name: t(section.nameKey), pages });
  }

  return { tocStructure, pageModuleAssignments };
};
